/*
 * certificate-maker/backgrounds.js — illustrated backgrounds as PARAMETRIC VECTOR.
 *
 * WHY THIS EXISTS
 * ───────────────
 * Compared against homiwork's certificate generator (2026-08-24), which paints
 * a whole sheet with a diffusion model: their sheets are prettier than a bare
 * guilloché frame, and that is a real gap. But their TEXT is baked into the
 * pixels, and everything follows from that — a typo cannot be fixed, a name
 * cannot be selected or corrected, thirty sheets for a class list cannot exist,
 * and the type is resolution-bound on paper.
 *
 * So the answer is not to copy the method. It is to close the decoration gap
 * while keeping the one thing they gave up: the text stays text. The picture is
 * drawn; the type is set.
 *
 * WHY VECTOR AND NOT A LIBRARY OF PICTURES
 * ────────────────────────────────────────
 * The same three reasons frames.js is parametric, and one more that only
 * applies here:
 *
 *   1. It does not pixelate. A painted A4 at 96 dpi turns to mush on paper, and
 *      the keyword tail is full of «в хорошем качестве» for that reason.
 *   2. It is not heavy. A painted background is 300 KB–2 MB; these are single-
 *      digit kilobytes of path data.
 *   3. It fits the page it is on. A4 landscape, A4 portrait and A5 each get art
 *      built FOR them rather than a picture stretched onto them.
 *   4. ⭐ It takes the document's colours. The art is drawn in the reader's own
 *      ink and accent, so choosing a palette recolours the illustration too.
 *      A raster background cannot do that at any price, and it is the reason a
 *      school's blue-and-gold certificate and a studio's rose-and-cream one can
 *      be the same background id.
 *
 * ⚠️ pdf-lib also forces the issue: it embeds PNG and JPEG, and draws vector
 * paths, but it has no gradient. Anything soft here is built from overlapping
 * translucent shapes rather than a gradient fill — which is also the only form
 * that survives all three renderers unchanged.
 *
 * ⚠️ THE ONE PLACE RASTER WINS, measured 2026-08-24 so nobody has to guess:
 * a picture is embedded ONCE per document however many pages use it, while
 * these paths are re-emitted per page. One A4: 46 KB bare, 47–62 KB with art.
 * Thirty A4: 995 KB bare, 1.3–1.5 MB with art, against 1.07 MB for a shared
 * photograph. So the vector costs ~15 KB a page in a long batch and buys sharp
 * print, a recolourable drawing and a file that is still under two megabytes.
 * If that ever stops being the right trade, the fix is a Form XObject — draw
 * once, reference thirty times — not a library of JPEGs.
 *
 * OUTPUT CONTRACT — frames.js publishes the first half of it:
 *
 *   insetOf(id)                                   → mm the art claims, or null
 *   build(id, {w, h, ink, accent, frames, keep})  → { prims, inset }
 *
 * Two calls, not one, and the split is the whole design. `insetOf` is a static
 * claim layout can read BEFORE it places the type; `keep` is the rectangle the
 * type ended up in, handed back so the art can draw around it. An illustration
 * cannot know where the words are until they are placed, and the words cannot
 * be placed until the illustration says how much room it needs — so the claim
 * comes first and the drawing waits.
 *
 * ⚠️ A background that lies about its inset produces exactly the defect this
 * whole layer exists to avoid: a name printed on top of the illustration. That
 * is the failure people photograph and send to support, and the reason
 * `tests/certificate-layout.mjs` checks every background's claim against the
 * ink it actually puts down.
 *
 * ⚠️ Nothing here may call Math.random(): the preview, the PNG and the PDF are
 * three separate passes over the same document, and a background that scatters
 * its confetti differently each pass is a background whose paper never matches
 * its screen. Scatter comes from `rnd(i)` below, which is a hash, not a
 * generator.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.CertBackgrounds = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var TAU = Math.PI * 2;

    /* Deterministic scatter. Same index → same number, for ever and in every
       renderer. Wang-style integer hash; the constants are the usual ones. */
    function rnd(i) {
        var x = (i | 0) * 374761393 + 668265263;
        x = (x ^ (x >>> 13)) * 1274126177;
        return ((x ^ (x >>> 16)) >>> 0) / 4294967296;
    }
    /** Deterministic value in [a, b) for stream `k` of item `i`. */
    function rr(i, k, a, b) { return a + rnd(i * 977 + k * 31 + 7) * (b - a); }

    function n2(v) { return Math.round(v * 100) / 100; }

    /* ── the drawing kit ─────────────────────────────────────────────────────
     * Borrowed from frames.js rather than copied: two path builders would drift,
     * and the first symptom of the drift is a preview that stops matching the
     * paper. `build()` takes the frames api as a dependency so this file also
     * loads standalone in the Node test, where there is no window. */
    function kit(opts) {
        var F = (opts && opts.frames)
            || (typeof window !== 'undefined' && window.CertFrames)
            || null;
        if (!F || !F._P) throw new Error('backgrounds: frames api required');
        return { P: F._P, path: F._path, star: F._star, leaf: F._leaf, rosette: F._rosette };
    }

    /* A soft blob: a circle with its radius modulated by two harmonics, drawn
     * through Catmull-Rom. Overlap a few at low opacity and the eye reads a
     * watercolour wash — which is as close to soft as a gradient-less renderer
     * can get, and it prints as flat ink rather than as a banded ramp. */
    function blob(K, cx, cy, r, seed) {
        var pts = [], steps = 26;
        for (var i = 0; i < steps; i++) {
            var a = i / steps * TAU;
            var rad = r * (1
                + 0.16 * Math.sin(a * 3 + rr(seed, 1, 0, TAU))
                + 0.09 * Math.sin(a * 5 + rr(seed, 2, 0, TAU)));
            pts.push([cx + Math.cos(a) * rad, cy + Math.sin(a) * rad]);
        }
        return new K.P().smooth(pts, true).d;
    }

    /** A stem: one cubic from (x0,y0) to (x1,y1) bowed by `bow` millimetres. */
    function stem(K, x0, y0, x1, y1, bow) {
        var mx = (x0 + x1) / 2, my = (y0 + y1) / 2;
        var dx = x1 - x0, dy = y1 - y0, len = Math.sqrt(dx * dx + dy * dy) || 1;
        var nx = -dy / len, ny = dx / len;
        return new K.P().m(x0, y0).c(
            x0 + dx * 0.25 + nx * bow * 0.7, y0 + dy * 0.25 + ny * bow * 0.7,
            mx + dx * 0.15 + nx * bow, my + dy * 0.15 + ny * bow,
            x1, y1).d;
    }

    /** Points along that same cubic, so leaves can sit ON the stem. */
    function stemPts(x0, y0, x1, y1, bow, count) {
        var mx = (x0 + x1) / 2, my = (y0 + y1) / 2;
        var dx = x1 - x0, dy = y1 - y0, len = Math.sqrt(dx * dx + dy * dy) || 1;
        var nx = -dy / len, ny = dx / len;
        var p0 = [x0, y0],
            p1 = [x0 + dx * 0.25 + nx * bow * 0.7, y0 + dy * 0.25 + ny * bow * 0.7],
            p2 = [mx + dx * 0.15 + nx * bow, my + dy * 0.15 + ny * bow],
            p3 = [x1, y1];
        var out = [];
        for (var i = 0; i <= count; i++) {
            var t = i / count, u = 1 - t;
            var x = u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0];
            var y = u * u * u * p0[1] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[1];
            var tx = 3 * u * u * (p1[0] - p0[0]) + 6 * u * t * (p2[0] - p1[0]) + 3 * t * t * (p3[0] - p2[0]);
            var ty = 3 * u * u * (p1[1] - p0[1]) + 6 * u * t * (p2[1] - p1[1]) + 3 * t * t * (p3[1] - p2[1]);
            var tl = Math.sqrt(tx * tx + ty * ty) || 1;
            out.push({ x: x, y: y, nx: -ty / tl, ny: tx / tl });
        }
        return out;
    }

    /** A wavy ribbon (streamer) as a stroked harmonic run. */
    function streamer(K, x0, y0, x1, y1, amp, cycles, phase) {
        var pts = [], steps = 40;
        for (var i = 0; i <= steps; i++) {
            var t = i / steps;
            var x = x0 + (x1 - x0) * t, y = y0 + (y1 - y0) * t;
            var dx = x1 - x0, dy = y1 - y0, len = Math.sqrt(dx * dx + dy * dy) || 1;
            var off = Math.sin(t * TAU * cycles + phase) * amp * Math.sin(Math.PI * t);
            pts.push([x + (-dy / len) * off, y + (dx / len) * off]);
        }
        return new K.P().smooth(pts, false).d;
    }

    /* ── the keep-out ring ───────────────────────────────────────────────────
     *
     * ⚠️ THE LESSON THAT COST THE FIRST NINE DRAFTS. It is tempting to reason
     * «the middle of a sheet is where the name goes, so put the art at the top
     * and the bottom». It is wrong, and rendering them proved it in one look: a
     * certificate's type runs from the organisation at the very top to the
     * signature at the very bottom, so a horizontal band ANYWHERE across the
     * sheet lands on words — a stave through the signature line, confetti
     * through the school's name.
     *
     * What is actually free is the RING between the paper edge and the text
     * column. Layout hands it over as `keep`, and everything below places its
     * ink by asking, never by assuming. */

    /** True when the whole disc of radius `r` around (x,y) misses the type. */
    function free(keep, x, y, r) {
        if (!keep) return true;
        return (x + r < keep.x0) || (x - r > keep.x1) || (y + r < keep.y0) || (y - r > keep.y1);
    }

    /** A point in the margin ring, chosen deterministically. `null` when the
     *  ring is too thin to hold one — a sheet whose text fills it has no room
     *  for decoration, and inventing some is how the collision comes back. */
    function inRing(o, i, r) {
        for (var k = 0; k < 24; k++) {
            var x = rr(i, 10 + k, 0.012, 0.988) * o.w;
            var y = rr(i, 40 + k, 0.012, 0.988) * o.h;
            if (free(o.keep, x, y, r)) return { x: x, y: y };
        }
        return null;
    }

    /** The four margin strips, as rectangles. Handy for art that wants a band
     *  rather than a scatter — a stave under the text, rays over it. */
    function strips(o) {
        var k = o.keep || { x0: o.w, y0: o.h, x1: o.w, y1: o.h };
        return {
            top:    { x0: 0, y0: 0, x1: o.w, y1: Math.max(0, k.y0) },
            bottom: { x0: 0, y0: Math.min(o.h, k.y1), x1: o.w, y1: o.h },
            left:   { x0: 0, y0: 0, x1: Math.max(0, k.x0), y1: o.h },
            right:  { x0: Math.min(o.w, k.x1), y0: 0, x1: o.w, y1: o.h }
        };
    }
    function tall(s) { return s.y1 - s.y0; }

    /* ── the backgrounds ─────────────────────────────────────────────────────
     * Every one of them keeps the MIDDLE of the sheet calm and puts its ink at
     * the edges. That is not a style preference: the middle is where the name
     * goes, and the name is the reason the sheet exists. */
    var BG = {
        none:      { inset: null, build: function () { return []; } },

        /* Watercolour corners. The most useful one, because it is the only
         * background that suits a document with no theme at all. */
        wash: {
            inset: 15,
            build: function (o, K) {
                var out = [], R = Math.min(o.w, o.h);
                var spots = [
                    [0.02, 0.03, 0.46, 0], [0.16, -0.05, 0.34, 1], [-0.04, 0.20, 0.30, 2],
                    [0.98, 0.97, 0.46, 3], [0.84, 1.05, 0.34, 4], [1.04, 0.80, 0.30, 5]
                ];
                spots.forEach(function (s, i) {
                    /* ⚠️ The ONE background that is allowed under the type, and
                       on purpose: at 5 % a wash cannot take a line of text below
                       any contrast threshold — the preflight computes exactly
                       that and still judges it. Keeping it out of the ring
                       instead deleted the two corner blobs that balanced the
                       sheet and left the art all on one side. */
                    var bx = s[0] * o.w, by = s[1] * o.h;
                    out.push(K.path(blob(K, bx, by, R * s[2], s[3]), {
                        fill: (i % 2) ? o.accent : o.ink, color: null, w: 0,
                        opacity: (i % 2) ? 0.055 : 0.045
                    }));
                });
                return out;
            }
        },

        /* Botanical sprays across two opposite corners — the shape the studio
         * and gift-certificate examples reach for. */
        botanical: {
            inset: 19,
            build: function (o, K) {
                var out = [], R = Math.min(o.w, o.h);
                /* ⚠️ Every leaf is checked, and so is the STEM. Placing the
                   spray by eye put three leaves and a blossom across «А. П.
                   Смирнова» — measured on the primitives, not noticed by
                   looking, because at 30 % opacity a leaf behind a signature is
                   exactly subtle enough to ship. */
                function spray(bx, by, tx, ty, bow, seed) {
                    /* ⚠️ A straight run from one margin to the other CUTS THE
                       CORNER, and the corner is the only part of a margin that
                       is inside the text column's diagonal. Truncating at the
                       first blocked point deleted the whole spray instead
                       (measured: 0 primitives). The fix is to bend it AROUND
                       the corner — and how much bend that takes depends on how
                       deep the margin is, which depends on the page, the frame
                       and the author's padding. So it is solved, not guessed:
                       try increasing bows and keep the first one that clears. */
                    var sp = null, usedBow = 0;
                    for (var bi = 0; bi < 7 && !sp; bi++) {
                        /* BOTH signs. Which way a positive bow bends depends on
                           the order of the endpoints, and hard-coding it sent
                           both sprays into the page instead of around it — the
                           spray then cleared nothing and drew nothing at all. */
                        for (var sg = 0; sg < 2 && !sp; sg++) {
                            var tryBow = bow * (0.6 + bi * 0.45) * (sg ? -1 : 1);
                            var cand = stemPts(bx, by, tx, ty, tryBow, 9);
                            var clean = true;
                            for (var ci = 0; ci < cand.length; ci++) {
                                if (!free(o.keep, cand[ci].x, cand[ci].y, R * 0.010)) { clean = false; break; }
                            }
                            if (clean) { sp = cand; usedBow = tryBow; }
                        }
                    }
                    if (!sp) return;
                    out.push(K.path(stem(K, bx, by, tx, ty, usedBow), { color: o.accent, w: 0.5, opacity: 0.75 }));
                    for (var i = 1; i < sp.length; i++) {
                        var q = sp[i], side = (i % 2) ? 1 : -1;
                        /* A leaf longer than the margin is a leaf on the text. */
                        var room = o.keep ? Math.min(o.keep.x0, o.keep.y0,
                            o.w - o.keep.x1, o.h - o.keep.y1) : R * 0.10;
                        var L = Math.min(R * 0.055, room * 0.42) * (1 - i / (sp.length * 1.7));
                        var lx = q.x + q.nx * side * L * 2.1 + (q.x - sp[i - 1].x) * 0.9;
                        var ly = q.y + q.ny * side * L * 2.1 + (q.y - sp[i - 1].y) * 0.9;
                        if (!free(o.keep, q.x, q.y, L) || !free(o.keep, lx, ly, L * 0.5)) continue;
                        var pp = new K.P();
                        K.leaf(pp, q.x, q.y, lx, ly, L * 0.85);
                        out.push(K.path(pp.d, { fill: o.accent, color: null, w: 0, opacity: 0.30 }));
                    }
                    for (var b2 = 0; b2 < 3; b2++) {
                        var qq = sp[3 + b2 * 2];
                        if (!qq) continue;
                        var br = R * 0.022 * (1 - b2 * 0.18);
                        if (!free(o.keep, qq.x, qq.y, br * 1.2)) continue;
                        out.push(K.path(K.rosette(qq.x, qq.y, br, 6, 0.42, 1),
                            { fill: o.accent, color: null, w: 0, opacity: 0.40 }));
                        out.push({ type: 'circle', cx: qq.x, cy: qq.y, r: R * 0.006,
                            fill: o.ink, color: null, w: 0, opacity: 0.35 });
                    }
                }
                /* ⚠️ Anchored to the MARGIN, not to a fraction of the sheet.
                   Endpoints picked as «30 % of the width» need an enormous bow
                   to clear a 25 mm margin, and the search duly found one — which
                   arced the spray almost entirely off the paper. A chord that
                   already lies in the corner needs only a small bend. */
                var mL = o.keep ? Math.max(R * 0.05, o.keep.x0) : R * 0.10;
                var mT = o.keep ? Math.max(R * 0.05, o.keep.y0) : R * 0.10;
                var mR = o.keep ? Math.max(R * 0.05, o.w - o.keep.x1) : R * 0.10;
                var mB = o.keep ? Math.max(R * 0.05, o.h - o.keep.y1) : R * 0.10;
                spray(-R * 0.03, mT * 2.1, mL * 2.1, -R * 0.03, R * 0.05, 1);
                spray(o.w + R * 0.03, o.h - mB * 2.1, o.w - mR * 2.1, o.h + R * 0.03, R * 0.05, 2);
                return out;
            }
        },

        /* Streamers and confetti — a child's certificate, where the classical
         * guilloché reads as a tax form. Everything lives in the margin ring:
         * the first draft ran its streamers across the sheet and put one
         * straight through the school's name. */
        confetti: {
            inset: 17,
            build: function (o, K) {
                var out = [], R = Math.min(o.w, o.h), S = strips(o);
                /* Streamers hug the top and bottom strips, and only if the
                   strip is deep enough to hold one. */
                [[S.top, 1], [S.bottom, -1]].forEach(function (pair, si) {
                    var st = pair[0], dir = pair[1], depth = tall(st);
                    if (depth < R * 0.045) return;
                    var lanes = depth > R * 0.10 ? 3 : 2;
                    for (var i = 0; i < lanes; i++) {
                        var base = dir > 0
                            ? st.y0 + depth * (0.22 + i * 0.26)
                            : st.y1 - depth * (0.22 + i * 0.26);
                        var amp = Math.min(depth * 0.20, R * 0.03);
                        out.push(K.path(streamer(K, -R * 0.03, base, o.w + R * 0.03, base,
                            amp, 1.5 + i * 0.4, si * 2.1 + i * 1.3), {
                            color: (i % 2) ? o.accent : o.ink, w: 0.55, opacity: 0.30
                        }));
                    }
                });
                for (var i2 = 0; i2 < 90; i2++) {
                    var sz = R * rr(i2, 3, 0.008, 0.019);
                    var q = inRing(o, i2, sz * 1.3);
                    if (!q) continue;
                    var col = (i2 % 3 === 0) ? o.ink : o.accent;
                    var op = rr(i2, 4, 0.24, 0.58);
                    if (i2 % 4 === 0) {
                        out.push({ type: 'circle', cx: q.x, cy: q.y, r: sz * 0.6, fill: col, color: null, w: 0, opacity: op });
                    } else if (i2 % 4 === 1) {
                        out.push(K.path(K.star(q.x, q.y, sz), { fill: col, color: null, w: 0, opacity: op }));
                    } else {
                        var a = rr(i2, 5, 0, TAU), cs = Math.cos(a), sn = Math.sin(a);
                        var hw = sz * 0.9, hh = sz * 0.5, pp = new K.P();
                        pp.poly([[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]].map(function (v) {
                            return [q.x + v[0] * cs - v[1] * sn, q.y + v[0] * sn + v[1] * cs];
                        }), true);
                        out.push(K.path(pp.d, { fill: col, color: null, w: 0, opacity: op }));
                    }
                }
                return out;
            }
        },

        /* A stave along the bottom margin, notes riding it. The first draft put
         * it at 80 % of the height, which is exactly where the signature line
         * lives — the notes went through «Директор школы». */
        music: {
            inset: 18,
            build: function (o, K) {
                var out = [], R = Math.min(o.w, o.h), S = strips(o);
                var depth = tall(S.bottom);
                if (depth > R * 0.05) {
                    var gap = Math.min(R * 0.020, depth * 0.16);
                    var base = S.y1 == null ? 0 : 0;
                    base = S.bottom.y0 + depth * 0.5 - gap * 2;
                    var amp = Math.min(depth * 0.16, R * 0.025);
                    for (var l = 0; l < 5; l++) {
                        out.push(K.path(streamer(K, -R * 0.03, base + l * gap, o.w + R * 0.03, base + l * gap,
                            amp, 1.15, 0.6), { color: o.ink, w: 0.3, opacity: 0.32 }));
                    }
                    for (var i = 0; i < 11; i++) {
                        var t = 0.06 + i * 0.083, line = i % 5;
                        var x = -R * 0.03 + (o.w + R * 0.06) * t;
                        var y = base + line * gap + Math.sin(t * TAU * 1.15 + 0.6) * amp * Math.sin(Math.PI * t);
                        var c = (i % 3 === 0) ? o.accent : o.ink;
                        out.push({ type: 'circle', cx: x, cy: y, r: gap * 0.45, fill: c, color: null, w: 0, opacity: 0.45 });
                        var stemTop = Math.max(S.bottom.y0 + 0.5, y - gap * 2.4);
                        out.push(K.path(new K.P().m(x + gap * 0.43, y).l(x + gap * 0.43, stemTop).d,
                            { color: c, w: 0.32, opacity: 0.45 }));
                    }
                }
                /* Notes drifting up the side margins, so the sheet is balanced
                   top and bottom. (A curled flourish in the top-left corner was
                   tried first and read as a smear at margin size — an ornament
                   that has to be explained is an ornament that failed.) */
                for (var f = 0; f < 12; f++) {
                    var sz = R * rr(f, 7, 0.010, 0.020);
                    var q = inRing(o, f + 500, sz * 2.4);
                    if (!q) continue;
                    var c2 = (f % 3 === 0) ? o.accent : o.ink;
                    out.push({ type: 'circle', cx: q.x, cy: q.y, r: sz * 0.62, fill: c2, color: null, w: 0, opacity: 0.34 });
                    out.push(K.path(new K.P().m(q.x + sz * 0.58, q.y).l(q.x + sz * 0.58, q.y - sz * 2.6).d,
                        { color: c2, w: 0.3, opacity: 0.34 }));
                }
                return out;
            }
        },

        /* Rays in the top margin, laurel in the bottom one. */
        rays: {
            inset: 18,
            build: function (o, K) {
                var out = [], R = Math.min(o.w, o.h), S = strips(o);
                var topDepth = tall(S.top);
                if (topDepth > R * 0.035) {
                    /* Rays are read as light coming from somewhere, which needs
                       them to reach the EDGES of the sheet — a narrow fan over
                       the middle of the top margin reads as a smudge instead.
                       The apex sits well above the paper so the spread across
                       the strip is wide, and the wedges are cut at its floor. */
                    /* ⚠️ Adjacent wedges must be separated by PAPER, not by a
                       lighter wedge. Filling every one of them — even in two
                       alternating tones — makes a solid bar across the top of
                       the sheet, which is what a printer's smudge looks like.
                       Rays are the gaps as much as the ink. */
                    var cx = o.w / 2, cy = S.top.y1, apex = -o.h * 0.85;
                    var reach = (o.w * 0.72) / Math.max(1, cy - apex);
                    var N = 13;
                    for (var i = 0; i < N; i++) {
                        var mid = (i - (N - 1) / 2) / ((N - 1) / 2);      // −1 … 1
                        var half = reach / N * 0.62;                      // ink; the rest is paper
                        var p = new K.P()
                            .m(cx, apex)
                            .l(cx + (mid * reach - half) * (cy - apex), cy)
                            .l(cx + (mid * reach + half) * (cy - apex), cy)
                            .z();
                        /* Brightest at the centre, so the light has a source. */
                        out.push(K.path(p.d, { fill: (i % 2) ? o.accent : o.ink, color: null, w: 0,
                            opacity: (0.20 - Math.abs(mid) * 0.11) * ((i % 2) ? 1 : 0.55) }));
                    }
                }
                var botDepth = tall(S.bottom);
                if (botDepth > R * 0.05) {
                    var by = S.bottom.y0 + botDepth * 0.72, bw = Math.min(o.w * 0.26, botDepth * 3.4);
                    [-1, 1].forEach(function (side) {
                        var x0 = o.w / 2 + side * bw * 0.12, y0 = by;
                        var x1 = o.w / 2 + side * bw, y1 = by - botDepth * 0.42;
                        var bow = side * bw * 0.16;
                        out.push(K.path(stem(K, x0, y0, x1, y1, bow), { color: o.accent, w: 0.45, opacity: 0.6 }));
                        var pts = stemPts(x0, y0, x1, y1, bow, 8);
                        for (var j = 1; j < pts.length; j++) {
                            var q = pts[j], L = Math.min(R * 0.042, botDepth * 0.30) * (1 - j / (pts.length * 2.2));
                            var pp = new K.P();
                            K.leaf(pp, q.x, q.y, q.x + q.nx * side * L * 2.4 - side * L * 0.6,
                                q.y + q.ny * side * L * 2.4 - L * 0.9, L * 0.8);
                            out.push(K.path(pp.d, { fill: o.accent, color: null, w: 0, opacity: 0.36 }));
                        }
                    });
                    out.push(K.path(K.star(o.w / 2, by - botDepth * 0.30, Math.min(R * 0.020, botDepth * 0.22)),
                        { fill: o.ink, color: null, w: 0, opacity: 0.35 }));
                }
                return out;
            }
        },

        /* A night of stars — graduations, and anything for a teenager. */
        stars: {
            inset: 15,
            build: function (o, K) {
                var out = [], R = Math.min(o.w, o.h);
                for (var i = 0; i < 120; i++) {
                    var sz = R * rr(i, 3, 0.004, 0.016);
                    var q = inRing(o, i, sz * 2.2);
                    if (!q) continue;
                    var op = rr(i, 4, 0.18, 0.55);
                    if (i % 5 === 0) {
                        var x = q.x, y = q.y, p = new K.P();
                        p.m(x, y - sz * 2).c(x + sz * 0.3, y - sz * 0.3, x + sz * 0.3, y - sz * 0.3, x + sz * 2, y)
                            .c(x + sz * 0.3, y + sz * 0.3, x + sz * 0.3, y + sz * 0.3, x, y + sz * 2)
                            .c(x - sz * 0.3, y + sz * 0.3, x - sz * 0.3, y + sz * 0.3, x - sz * 2, y)
                            .c(x - sz * 0.3, y - sz * 0.3, x - sz * 0.3, y - sz * 0.3, x, y - sz * 2).z();
                        out.push(K.path(p.d, { fill: o.accent, color: null, w: 0, opacity: op }));
                    } else if (i % 5 === 1) {
                        out.push(K.path(K.star(q.x, q.y, sz * 1.5), { fill: o.accent, color: null, w: 0, opacity: op }));
                    } else {
                        out.push({ type: 'circle', cx: q.x, cy: q.y, r: sz * 0.55,
                            fill: o.ink, color: null, w: 0, opacity: op });
                    }
                }
                return out;
            }
        },

        /* A corner ribbon — the gift certificate. */
        ribbon: {
            inset: 16,
            build: function (o, K) {
                var out = [], R = Math.min(o.w, o.h);
                /* The band is sized to the corner the type does NOT use: it may
                   reach the text column's corner but never cross it. */
                /* A band is read by its WIDTH, and a corner cut only reaches
                   the type if it is longer than the corner is deep. So the cut
                   runs the diagonal of the corner the column leaves free, and
                   the band across it is a fixed proportion of that — the first
                   version clamped the whole thing and produced a hairline. */
                var freeX = o.keep ? Math.max(R * 0.10, o.w - o.keep.x1) : R * 0.30;
                var freeY = o.keep ? Math.max(R * 0.10, o.keep.y0) : R * 0.30;
                var d = Math.min(R * 0.62, (freeX + freeY) * 1.55);
                out.push(K.path(new K.P()
                    .m(o.w - d, 0).l(o.w - d * 0.52, 0).l(o.w, d * 0.52).l(o.w, d).z().d,
                    { fill: o.accent, color: null, w: 0, opacity: 0.30 }));
                out.push(K.path(new K.P().m(o.w - d * 1.20, 0).l(o.w - d * 1.05, 0)
                    .l(o.w, d * 1.05).l(o.w, d * 1.20).z().d,
                    { fill: o.ink, color: null, w: 0, opacity: 0.20 }));
                /* Mirrored, thinner, in the opposite corner: one band alone makes
                   the sheet look as though it slipped. ⚠️ Sized from the room
                   THAT corner has, not from the first band — the bottom-left of
                   a certificate carries the place and the date, and a band cut
                   to the top-right's measurements ran straight through them. */
                var freeX2 = o.keep ? Math.max(R * 0.06, o.keep.x0) : R * 0.20;
                var freeY2 = o.keep ? Math.max(R * 0.06, o.h - o.keep.y1) : R * 0.20;
                var d2 = Math.min(d * 0.78, (freeX2 + freeY2) * 1.05);
                out.push(K.path(new K.P().m(0, o.h - d2).l(0, o.h - d2 * 0.80)
                    .l(d2 * 0.80, o.h).l(d2, o.h).z().d,
                    { fill: o.accent, color: null, w: 0, opacity: 0.22 }));
                /* ⚠️ The bow is sized from the MARGIN, not from the band. Cut
                   to a fraction of `d` it never fitted the corner and the check
                   below silently dropped it every single time — a bow nobody
                   ever saw, on the one background that is meant to have one. */
                var room = Math.min(freeX, freeY);
                var sz = Math.min(R * 0.05, room * 0.16);
                var bx = o.w - room * 0.45, by = room * 0.45;
                if (sz > 0.8 && free(o.keep, bx, by, sz * 2.4)) {
                    [[-1, -1], [1, -1], [-1, 1], [1, 1]].forEach(function (q, i) {
                        var pp = new K.P().m(bx, by).c(
                            bx + q[0] * sz * 2.4, by + q[1] * sz * 0.5,
                            bx + q[0] * sz * 2.0, by + q[1] * sz * 2.2, bx, by).z();
                        out.push(K.path(pp.d, { fill: o.accent, color: null, w: 0, opacity: 0.60 - i * 0.03 }));
                    });
                    out.push({ type: 'circle', cx: bx, cy: by, r: sz * 0.5, fill: o.ink, color: null, w: 0, opacity: 0.6 });
                }
                return out;
            }
        },

        /* Speed bands down the side margins, a wreath in the bottom one. */
        sport: {
            inset: 18,
            build: function (o, K) {
                var out = [], R = Math.min(o.w, o.h), S = strips(o);
                var lw = Math.max(0, S.left.x1), rw = Math.max(0, o.w - S.right.x0);
                [[lw, 1], [rw, -1]].forEach(function (pair, si) {
                    var wide = pair[0], dir = pair[1];
                    if (wide < R * 0.02) return;
                    /* Three bands, each a third of the strip — five hairlines
                       inside an 18 mm margin is not a graphic, it is a texture
                       nobody can see. They lean, because a straight bar down the
                       side is a rule, and a leaning one is speed. */
                    /* ⚠️ A LEANING band is wider than it looks: its far end is
                       `lean + th` further in than its near end, and that is what
                       crossed the date line. The lean is sized so the far end
                       still stops short of the column. */
                    var th = wide * 0.22;
                    var lean = Math.max(0, wide - th) * 0.5;
                    for (var i = 0; i < 3; i++) {
                        var near = wide * (0.06 + i * 0.28);
                        if (near + lean + th > wide) break;
                        var x = dir > 0 ? near : o.w - near;
                        out.push(K.path(new K.P()
                            .m(x, -R * 0.02).l(x + dir * lean, o.h + R * 0.02)
                            .l(x + dir * (lean + th), o.h + R * 0.02).l(x + dir * th, -R * 0.02).z().d,
                            { fill: (i % 2) ? o.accent : o.ink, color: null, w: 0,
                              opacity: (i % 2) ? 0.16 : 0.09 }));
                    }
                });
                var botDepth = tall(S.bottom);
                if (botDepth > R * 0.05) {
                    var cy = S.bottom.y0 + botDepth * 0.66, cx = o.w / 2;
                    var rad = Math.min(o.w * 0.11, botDepth * 0.62);
                    [-1, 1].forEach(function (side) {
                        var x0 = cx + side * rad * 0.22, y0 = cy + rad * 0.34;
                        var x1 = cx + side * rad * 1.15, y1 = cy - rad * 0.52;
                        var bow = side * rad * 0.55;
                        out.push(K.path(stem(K, x0, y0, x1, y1, bow), { color: o.accent, w: 0.45, opacity: 0.55 }));
                        var pts = stemPts(x0, y0, x1, y1, bow, 9);
                        for (var j = 1; j < pts.length; j++) {
                            var q = pts[j], L = rad * 0.30 * (1 - j / (pts.length * 2.3));
                            var pp = new K.P();
                            K.leaf(pp, q.x, q.y, q.x + q.nx * side * L * 2.3 - side * L * 0.5,
                                q.y + q.ny * side * L * 2.3 - L * 0.8, L * 0.78);
                            out.push(K.path(pp.d, { fill: o.accent, color: null, w: 0, opacity: 0.34 }));
                        }
                    });
                    out.push(K.path(K.star(cx, cy - rad * 0.60, rad * 0.26),
                        { fill: o.ink, color: null, w: 0, opacity: 0.38 }));
                }
                return out;
            }
        }
    };

    var ORDER = ['none', 'wash', 'botanical', 'confetti', 'music', 'rays', 'stars', 'ribbon', 'sport'];

    function list() { return ORDER.slice(); }

    /**
     * build(id, {w, h, ink, accent, frames, keep}) → { prims, inset }
     *
     * `inset` is null for `none`, which means "claim nothing" — the frame's own
     * inset then decides, exactly as before this file existed.
     */
    function build(id, opts) {
        var b = BG[id] || BG.none;
        var K = kit(opts);
        var o = {
            w: opts.w, h: opts.h,
            ink: opts.ink || '#1d2b45',
            accent: opts.accent || '#b08b3f',
            keep: opts.keep || null
        };
        var prims = b.build(o, K) || [];
        /* Line weights follow the sheet, the way the frames' do: an A5 card gets
           a proportionally lighter drawing rather than the A4 one shrunk. */
        var k = Math.min(1.25, Math.max(0.72, Math.min(o.w, o.h) / 210));
        if (k !== 1) prims.forEach(function (p) { if (p.w) p.w = n2(p.w * k); });
        return { prims: prims, inset: b.inset == null ? null : n2(b.inset * k) };
    }

    /** The claim, without drawing anything. Layout needs it BEFORE it can tell
     *  the art where the type will be — see the keep-out note above. */
    function insetOf(id) {
        var b = BG[id] || BG.none;
        return b.inset == null ? null : b.inset;
    }

    return { list: list, build: build, insetOf: insetOf, _rnd: rnd, _blob: blob, _free: free };
});
